import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, ArrowLeftRight, Gift, Trophy, MapPin, Radio, Loader2, Link2 } from "lucide-react";
import { LockedTile } from "@/components/market/LockedTile";
import { AuraRing } from "@/components/brand/AuraRing";
import { VerifiedBadge } from "@/components/brand/VerifiedBadge";
import { RightSidebar } from "@/components/app/RightSidebar";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { GenesisMarketFeature } from "@/components/genesis/GenesisMarketFeature";
import { AmbassadorBoard } from "@/components/genesis/AmbassadorBoard";
import { AnnouncementBar } from "@/components/app/AnnouncementBar";
import { FoundersLiveBanner } from "@/components/live/FoundersLiveBanner";

const TILES = [
  { key: "chart", icon: LineChart, title: "Live Chart", desc: "Real-time $AURAMAX price and volume once trading is live." },
  { key: "trades", icon: ArrowLeftRight, title: "Live Buys & Sells", desc: "A live tape of on-chain activity across the AuraMax economy." },
  { key: "giveaways", icon: Gift, title: "Giveaways", desc: "Community drops and creator-run giveaways, all in one place." },
  { key: "challenges", icon: Trophy, title: "Challenges", desc: "Timed creative challenges with leaderboards and recognition." },
  { key: "events", icon: MapPin, title: "IRL Events", desc: "Meetups, pop-ups and creator events near you." },
  { key: "streams", icon: Radio, title: "Celebrity Onboarding Streams", desc: "Watch well-known creators set up their aura live." },
];

export default function Market() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.market().then(setData).catch(() => setData({ top_referrers: [], my_rank: null })); }, []);

  return (
    <div className="flex">
      <div className="min-w-0 flex-1">
        <div className="mx-auto max-w-2xl px-4 py-5 md:px-6" data-testid="market-page">
          <h1 className="font-display text-2xl font-extrabold text-white">Market</h1>
          <p className="mt-1 text-sm text-am-text2">The AuraMax economy, taking shape. Features below are coming soon.</p>

          <div className="mt-4"><FoundersLiveBanner className="!max-w-none" /><AnnouncementBar className="!max-w-none" /></div>
          <GenesisMarketFeature />
          <AmbassadorBoard />

          <section className="mt-5 rounded-3xl border border-am-border bg-am-surface p-4" data-testid="market-top-referrers">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-white md:text-lg">Top Referrers</h2>
              {data?.my_rank && <span className="text-xs text-am-text2">Your rank: <b className="text-white" data-testid="market-my-rank">#{data.my_rank}</b></span>}
            </div>
            {!data ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>
              : data.top_referrers.length === 0 ? <p className="py-6 text-center text-sm text-am-text3" data-testid="market-board-empty">No qualified referrals yet. Your name could be first here.</p>
              : (
                <div className="mt-3 divide-y divide-am-border">
                  {data.top_referrers.map((r) => (
                    <button key={r.user.user_id} onClick={() => navigate(`/@${r.user.username}`)} data-testid={`market-row-${r.rank}`} className={cn("flex w-full items-center gap-3 py-2 text-left", r.user.user_id === user?.user_id && "bg-white/[0.03]")}>
                      <span className={cn("w-6 text-sm font-bold", r.rank <= 3 ? "aura-text" : "text-am-text2")}>#{r.rank}</span>
                      <AuraRing src={mediaUrl(r.user.picture) || undefined} size={32} ring={1.5} fallback={r.user.name} />
                      <div className="min-w-0 flex-1"><div className="flex items-center gap-1 truncate text-sm font-semibold text-white">{r.user.name}{r.user.is_verified && <VerifiedBadge size={12} />}</div><div className="truncate text-xs text-am-text2">@{r.user.username}</div></div>
                      <span className="text-sm font-bold text-white">{r.qualified}</span>
                    </button>
                  ))}
                </div>
              )}
            <button onClick={() => navigate("/referrals")} data-testid="market-get-link" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full aura-gradient py-2.5 text-sm font-bold text-black"><Link2 className="h-4 w-4" /> Get my referral link</button>
          </section>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {TILES.map((t) => <LockedTile key={t.key} icon={t.icon} title={t.title} desc={t.desc} testid={`market-tile-${t.key}`} />)}
          </div>
        </div>
      </div>
      <RightSidebar />
    </div>
  );
}
